import { useState } from "react";
import type { Owner } from "../data/types";

/**
 * A person's avatar: their Hub photo when they have one, their initials in
 * the member's palette colour otherwise.
 *
 * The colour travels ON the owner object (owner.color, a palette key) so an
 * arbitrary team member gets a stable colour. Unknown keys fall back to gray.
 * The photo is `owner.avatarUrl` — the Hub's public avatar endpoint, set once
 * the member is linked to their Hub person. A photo that fails to load (the
 * person removed it, the Hub is down) falls back to the initials rather than
 * a broken image.
 */
const PALETTE: Record<string, string> = {
  grayblue: "bg-grayblue-100 text-grayblue-700",
  violet: "bg-violet-100 text-violet-700",
  turquoise: "bg-turquoise-100 text-turquoise-700",
  green: "bg-green-100 text-green-700",
  brown: "bg-brown-100 text-brown-700",
  gray: "bg-gray-200 text-gray-700",
};

const DIMS = { sm: "h-5 w-5 text-[9px]", md: "h-6 w-6 text-[10px]" } as const;

export function Avatar({ owner, size = "md" }: { owner: Pick<Owner, "name" | "initials" | "color" | "avatarUrl">; size?: "sm" | "md" }) {
  // Keyed by URL so a different photo gets a fresh attempt after a failure.
  return <AvatarImage key={owner.avatarUrl ?? ""} owner={owner} dims={DIMS[size]} />;
}

function AvatarImage({ owner, dims }: { owner: Pick<Owner, "name" | "initials" | "color" | "avatarUrl">; dims: string }) {
  const [failed, setFailed] = useState(false);
  const base = `inline-flex flex-none items-center justify-center overflow-hidden rounded-full ${dims}`;

  if (owner.avatarUrl && !failed) {
    return (
      <img
        src={owner.avatarUrl}
        alt={owner.name}
        title={owner.name}
        loading="lazy"
        referrerPolicy="no-referrer"
        onError={() => setFailed(true)}
        className={`${base} bg-gray-200 object-cover`}
      />
    );
  }

  const color = PALETTE[owner.color] ?? PALETTE.gray;
  return (
    <span title={owner.name} aria-label={owner.name} className={`${base} font-semibold tracking-[0.02em] ${color}`}>
      {owner.initials}
    </span>
  );
}
